import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  calculateCartTotals,
  DEFAULT_SHIPPING_PRICE,
  normalizeCartItem,
} from "./cartUtils";
import {
  clearCartItemFiles,
  removeCartItemFiles,
} from "./cartFileStore";
import {
  clearStoredCart,
  loadStoredCart,
  saveStoredCart,
} from "./cartStorage";

const CartContext = createContext(null);

const initialCartState = {
  items: [],
};

function getInitialCartState() {
  const storedCart = loadStoredCart();

  if (!storedCart) {
    return initialCartState;
  }

  return {
    items: storedCart.items.map(normalizeCartItem),
  };
}

function cartReducer(state, action) {
  switch (action.type) {
    case "addItem": {
      return {
        ...state,
        items: [...state.items, normalizeCartItem(action.item)],
      };
    }

    case "updateQuantity": {
      const nextQuantity = Number(action.quantity);

      if (!Number.isFinite(nextQuantity) || nextQuantity <= 0) {
        return {
          ...state,
          items: state.items.filter(
            (item) => item.cartItemId !== action.cartItemId
          ),
        };
      }

      return {
        ...state,
        items: state.items.map((item) => {
          if (item.cartItemId !== action.cartItemId) {
            return item;
          }

          return normalizeCartItem({
            ...item,
            quantity: nextQuantity,
          });
        }),
      };
    }

    case "removeItem": {
      removeCartItemFiles(action.cartItemId);

      return {
        ...state,
        items: state.items.filter(
          (item) => item.cartItemId !== action.cartItemId
        ),
      };
    }

    case "clearCart": {
      clearCartItemFiles();

      return initialCartState;
    }

    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(
    cartReducer,
    undefined,
    getInitialCartState
  );

  useEffect(() => {
    if (!cart.items.length) {
      clearStoredCart();
      return;
    }

    saveStoredCart(cart);
  }, [cart]);

  const totals = useMemo(() => {
    return calculateCartTotals(cart.items, DEFAULT_SHIPPING_PRICE);
  }, [cart.items]);

  const value = useMemo(
    () => ({
      items: cart.items,
      totals,
      addItem: (item) => dispatch({ type: "addItem", item }),
      updateQuantity: (cartItemId, quantity) =>
        dispatch({ type: "updateQuantity", cartItemId, quantity }),
      removeItem: (cartItemId) =>
        dispatch({ type: "removeItem", cartItemId }),
      clearCart: () => dispatch({ type: "clearCart" }),
    }),
    [cart.items, totals]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }

  return context;
}
